import { apiWrapper } from './apiWrapper';
import { apiRequest, SUPABASE_URL } from './apiUtils';
import type {
  FinancialStatement,
  StatementWithSnapshots,
  StatementExportData,
} from '../../models/FinancialStatement';
import type { FinancialTransactionWithChildren } from '../../models/FinancialTransaction';

const BASE_URL = '/financial-statements';

export const financialStatementApi = {
  /**
   * Get all financial statements
   */
  async getStatements(params?: {
    status?: string;
  }): Promise<FinancialStatement[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);

    const url = `${BASE_URL}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiWrapper(url);
  },

  /**
   * Get a specific statement with optional balance snapshots
   */
  async getStatement(
    statementId: number,
    includeSnapshots = true
  ): Promise<StatementWithSnapshots> {
    const url = `${BASE_URL}/${statementId}?include_snapshots=${includeSnapshots}`;
    return apiWrapper(url);
  },

  /**
   * Get transactions for a statement
   */
  async getStatementTransactions(
    statementId: number,
    params?: {
      account_id?: number;
      include_children?: boolean;
      parents_only?: boolean;
    }
  ): Promise<FinancialTransactionWithChildren[]> {
    const queryParams = new URLSearchParams();
    if (params?.account_id) queryParams.append('account_id', params.account_id.toString());
    if (params?.include_children !== undefined)
      queryParams.append('include_children', params.include_children.toString());
    if (params?.parents_only) queryParams.append('parents_only', 'true');

    const url = `${BASE_URL}/${statementId}/transactions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiWrapper(url);
  },

  /**
   * Export statement as CSV
   * Returns the CSV content directly for download
   */
  async exportStatementCSV(statementId: number, accountId?: number): Promise<string> {
    const queryParams = new URLSearchParams();
    queryParams.append('format', 'csv');
    if (accountId) queryParams.append('account_id', accountId.toString());

    // Let the Content-Type drive parsing: text/csv on success, application/json
    // on error, so the error message reaches the caller instead of being read
    // as CSV text.
    return apiRequest<string>(
      `${SUPABASE_URL}${BASE_URL}/${statementId}/export?${queryParams.toString()}`
    );
  },

  /**
   * Get statement export data for PDF generation
   */
  async getStatementExportData(
    statementId: number,
    accountId?: number
  ): Promise<StatementExportData> {
    const queryParams = new URLSearchParams();
    queryParams.append('format', 'pdf');
    if (accountId) queryParams.append('account_id', accountId.toString());

    return apiWrapper(`${BASE_URL}/${statementId}/export?${queryParams.toString()}`);
  },

  /**
   * Create a new statement for a specific month
   */
  async createStatement(statementMonth: string, notes?: string): Promise<FinancialStatement> {
    return apiWrapper(BASE_URL, {
      method: 'POST',
      body: { statement_month: statementMonth, notes },
    });
  },

  /**
   * Finalize a statement
   */
  async finalizeStatement(statementId: number): Promise<FinancialStatement> {
    return apiWrapper(`${BASE_URL}/${statementId}/finalize`, {
      method: 'POST',
    });
  },

  /**
   * Update a statement's status or notes
   */
  async updateStatement(
    statementId: number,
    data: { status?: string; notes?: string }
  ): Promise<FinancialStatement> {
    return apiWrapper(`${BASE_URL}/${statementId}`, {
      method: 'PUT',
      body: data,
    });
  },
};
